/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import SweetAlert2 from "react-sweetalert2";
import { FaCopy, FaShareAlt, FaRedoAlt, FaSpinner, FaVolumeUp } from "react-icons/fa";
import { IoMdCopy } from "react-icons/io";
import { message } from "antd";
import { BASEURL } from "../../../utilites";
import axios from "axios";

const toStringSafe = (val) => {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (Array.isArray(val)) return val.join(" ");
  if (typeof val === "object") {
    if (typeof val.body === "string") return val.body;
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
};

const getCopySource = (data, aiResponse) => {
  if (typeof data?.body === "string" && data.body.trim()) return data.body;
  if (typeof aiResponse === "string" && aiResponse.trim()) return aiResponse;
  if (aiResponse && typeof aiResponse === "object" && typeof aiResponse.body === "string")
    return aiResponse.body;
  return "";
};

const stripMarkdownToText = (input) => {
  let s = toStringSafe(input);
  s = s.replace(/```[\s\S]*?```/g, "");
  s = s.replace(/!\[[^\]]*\]\([^)]+\)/g, "");
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  s = s.replace(/`([^`]+)`/g, "$1");
  s = s.replace(/(^|\n)[>#-]\s?/g, "$1");
  s = s.replace(/[*_~]{1,3}([^*_~]+)[*_~]{1,3}/g, "$1");
  s = s.replace(/[*_~#>]/g, "");
  s = s.replace(/\n{3,}/g, "\n\n").trim();
  return s;
};

const AIResponseActions = ({ 
  data,
  readPersonalChats, 
  isAITyping, getAIResponse, 
  toggleAiBotMode,
  LoginData,
}) => {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [swalProps, setSwalProps] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);
  const ownerIdRef = useRef(`owner_${Math.random().toString(36).slice(2)}`);
  const controllerRef = useRef(null);

    const isModalOpen = !!swalProps[data?.docId]?.show;
  const isActive = regenerating || isPlaying || isLoadingAudio || isModalOpen;

  const stopGlobalAudio = () => {
    if (typeof window === "undefined") return;
    const g = window.__AI_TTS_AUDIO__;
    if (g?.audio) {
      try {
        g.audio.pause();
        g.audio.currentTime = 0;
      } catch {}
    }
    if (g?.url) {
      try {
        URL.revokeObjectURL(g.url);
      } catch {}
    }
    window.__AI_TTS_AUDIO__ = null;
  };

  const claimGlobalAudio = (audio, url) => {
    if (typeof window === "undefined") return;
    window.__AI_TTS_AUDIO__ = { audio, url, owner: ownerIdRef.current };
  };

  const clearGlobalIfOwner = () => {
    if (typeof window === "undefined") return;
    const g = window.__AI_TTS_AUDIO__;
    if (g?.owner === ownerIdRef.current) {
      stopGlobalAudio();
    }
  };

  const handleCopy = () => {
    const source = getCopySource(data, true);
    const plainText = stripMarkdownToText(source);
    navigator.clipboard.writeText(plainText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const handleRegenerate = async () => {
    if (regenerating || isAITyping) return;
    setRegenerating(true);
    const idx = readPersonalChats.findIndex((m) => m.docId === data.docId);
    const lastUserMessage = [...readPersonalChats]
      .slice(0, idx)
      .reverse()
      .find((m) => m.messageType === "simple" && m.senderId !== "ai");
    try {
      if (lastUserMessage) {
        await getAIResponse({ message: lastUserMessage.body });
      }
    } finally {
      setRegenerating(false);
    }
  };

  const handleShare = () => {
    toggleAiBotMode(false,{autoSubmit:true,data:data});
  }

  const handleShareClick = (uuid) => {
    setSwalProps((prev) => ({
      ...prev,
      [uuid]: {
        show: true,
        showDenyButton: true,
        title: "Are you sure?",
        text: `Do you want to share this AI response with the ${!LoginData?.isCustomer ? "customer" : "Tax Expert"}?`,
        confirmButtonText: "Yes, share it!",
        denyButtonText: "Cancel",
        didClose: () =>
          setSwalProps((prev2) => ({ ...prev2, [uuid]: { show: false } })),
        onConfirm: () => {
          handleShare();
        }, 
      },
    }));
  };

  const stopAudio = () => {
    if (controllerRef.current) {
      try { controllerRef.current.abort(); } catch {}
      controllerRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch {}
    }
    if (objectUrlRef.current) {
      try {
        URL.revokeObjectURL(objectUrlRef.current);
      } catch {}
      objectUrlRef.current = null;
    }
    clearGlobalIfOwner();
    audioRef.current = null;
    setIsPlaying(false);
  };

  const handlePlay = async () => {
    if (isLoadingAudio) return;
    if (isPlaying) {
      stopAudio();
      return;
    }

    const source = getCopySource(data, true);
    const plainText = stripMarkdownToText(source);
    if (!plainText) {
      message.warning("Nothing to read.");
      return;
    }

    try {
      setIsLoadingAudio(true);
      stopGlobalAudio();
      if (controllerRef.current) {
        try { controllerRef.current.abort(); } catch {}
      }
      const controller = new AbortController();
      controllerRef.current = controller;

      const res = await axios.post(
        `${BASEURL}/api/voiceassistant/elevenlabs/stream`,
        {
          text: plainText,
          voiceId: null,
          modelId: null,
        },
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        }
      );

      if (!res || !res.data) {
        message.error("Failed to generate audio.");
        setIsLoadingAudio(false);
        return;
      }

      const blob = new Blob([res.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;

      const a = new Audio(url);
      audioRef.current = a;
      claimGlobalAudio(a, url);
      a.onended = () => {
        setIsPlaying(false);
        clearGlobalIfOwner();
      };
      await a.play();

      setIsPlaying(true);
    } catch (error) {
      if (error?.code !== "ERR_CANCELED") {
        message.error("Audio playback error.");
      }
    } finally {
      controllerRef.current = null;
      setIsLoadingAudio(false);
    }
  };

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  return (
   <>
      <div
        className={`d-flex justify-content-center align-items-center gap-3 p-2 shadow bg-white rounded ai-response-actions-wrapper ${isActive ? "active" : ""}`}
      >
        {isLoadingAudio ? (
          <FaSpinner
            className="text-muted"
            title="Loading voice..."
            style={{ opacity: 0.8, animation: "spin 1s linear infinite" }}
          />
        ) : (
          <button type="button" onClick={handlePlay} title="Read Aloud (ElevenLabs)">
            <FaVolumeUp className={` ${isPlaying ? "text-primary" : "text-muted"}`} />
          </button>
        )}

        <div className="position-relative">
          {copied ? (
            <IoMdCopy className="text-muted" title="Copied" />
          ) : (
            <button type="button" onClick={handleCopy} title="Copy">
              <FaCopy className="text-muted" />
            </button>
          )}
        </div>

        <div>
          <button type="button" onClick={() => handleShareClick(data?.docId)} title="Share">
            <FaShareAlt className="text-muted" />
          </button>
          <SweetAlert2 {...swalProps[data?.docId]} />
        </div>

        {regenerating ? (
          <FaSpinner
            className="text-muted"
            title="Regenerating..."
            style={{ opacity: 0.8, animation: "spin 1s linear infinite" }}
          />
        ) : (
          <button
            type="button"
            onClick={handleRegenerate}
            title="Regenerate"
            style={{ opacity: isAITyping ? 0.5 : 1, pointerEvents: isAITyping ? "none" : "auto" }}
          >
            <FaRedoAlt className="text-muted" />
          </button>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default AIResponseActions;
